import type { TelemetryFrame, TelemetryListener, TelemetryProvider } from './TelemetryProvider';
import { SimulatedVehicle, clamp01 } from './SimulatedVehicle';

export type MockDriveMode = 'auto' | 'manual';

interface ScriptStep {
  /** target speed km/h */
  target: number;
  /** 0..1 how hard the virtual driver pushes */
  aggr: number;
  /** seconds to hold once reached */
  hold: number;
}

/** A loop of a "spirited drive": launches, cruising, braking, stop. */
const SCRIPT: ScriptStep[] = [
  { target: 0, aggr: 1, hold: 2.5 },
  { target: 55, aggr: 1, hold: 2.5 },
  { target: 22, aggr: 0.7, hold: 1.5 },
  { target: 95, aggr: 1, hold: 3 },
  { target: 135, aggr: 0.85, hold: 3 },
  { target: 70, aggr: 0.6, hold: 2 },
  { target: 165, aggr: 1, hold: 2 },
  { target: 40, aggr: 0.9, hold: 1.5 },
  { target: 0, aggr: 0.8, hold: 3.5 },
];

/** Simple proportional "driver" following the script. */
class ScriptedDriver {
  private step = 0;
  private holdLeft = SCRIPT[0].hold;
  private jitter = 1;

  update(speedKmh: number, dtS: number): { pedal: number; brake: number } {
    const s = SCRIPT[this.step];
    const target = s.target * this.jitter;
    const err = target - speedKmh;
    let pedal = 0;
    let brake = 0;

    if (Math.abs(err) < 3 || (target === 0 && speedKmh < 1)) {
      this.holdLeft -= dtS;
      // light cruise throttle to hold speed
      pedal = target > 0 ? 0.1 + speedKmh / 600 : 0;
      if (this.holdLeft <= 0) this.advance();
    } else if (err > 0) {
      pedal = clamp01(0.25 * s.aggr + (err / 30) * s.aggr);
    } else {
      brake = clamp01((-err / 45) * (0.4 + 0.6 * s.aggr));
    }
    return { pedal, brake };
  }

  private advance(): void {
    this.step = (this.step + 1) % SCRIPT.length;
    this.holdLeft = SCRIPT[this.step].hold * (0.7 + Math.random() * 0.6);
    this.jitter = 0.9 + Math.random() * 0.2;
  }
}

/**
 * MockTelemetryProvider — simulated vehicle + either a scripted driver
 * ("auto") or external pedal/brake input ("manual": keyboard / touch).
 * Emits full-quality frames at 20 Hz, like a good CAN-based source would.
 */
export class MockTelemetryProvider implements TelemetryProvider {
  readonly id = 'mock';
  readonly label = 'Simulated';
  readonly quality = 'full' as const;

  mode: MockDriveMode = 'auto';
  /** External inputs used in manual mode (0..1). */
  manualPedal = 0;
  manualBrake = 0;

  private readonly vehicle = new SimulatedVehicle();

  setTopSpeedKmh(kmh: number): void {
    this.vehicle.setTopSpeedKmh(kmh);
  }
  private readonly driver = new ScriptedDriver();
  private readonly listeners = new Set<TelemetryListener>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly periodMs = 50;

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.step(), this.periodMs);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  subscribe(listener: TelemetryListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  setMode(mode: MockDriveMode): void {
    this.mode = mode;
    if (mode === 'manual') {
      this.manualPedal = 0;
      this.manualBrake = 0;
    }
  }

  private step(): void {
    const dt = this.periodMs / 1000;
    const input =
      this.mode === 'auto'
        ? this.driver.update(this.vehicle.speedKmh, dt)
        : { pedal: this.manualPedal, brake: this.manualBrake };
    this.vehicle.tick(input.pedal, input.brake, dt);
    const frame: TelemetryFrame = {
      t: performance.now(),
      speedKmh: this.vehicle.speedKmh,
      accelMs2: this.vehicle.accelMs2,
      pedal: input.pedal,
      brake: input.brake,
      source: this.mode === 'auto' ? 'mock:auto' : 'mock:manual',
    };
    this.listeners.forEach((l) => l(frame));
  }
}
